import type { DatasetListItem } from "@lumidb/lumidb";
import type * as GeoJSON from "geojson";
import * as THREE from "three";
import { closedRing, openedRing, type Feature } from "./annotations";
import { App, DatasetKind, type DatasetListMeta, type DatasetMeta } from "./app";
import { Auth } from "./auth";
import { registerAvatarDomains } from "./components/MemberAvatar";
import { HUD } from "./hud";
import { isEmptyObject, setArray, setRecord } from "./utils";

type FeatureRow = {
    feature_id: string;
    collection_id: string | null;
    geometry: GeoJSON.Geometry | null;
    properties: GeoJSON.GeoJsonProperties;
};

type FeatureCollection = {
    collection_id: string;
    bounds_wgs84?: [number, number, number, number];
    name: string;
    properties: GeoJSON.GeoJsonProperties | null;
    source_asset_ids: string[];
    feature_count: number;
    created_at: string;
    updated_at: string;
};

// Parse the server's feature-row array (from /features and the tiled endpoint)
// into GeoJSON features keyed by feature_id.
function parseFeatureRows(rows: FeatureRow[]): Record<string, Feature> {
    const features: Record<string, Feature> = {};
    for (const row of rows) {
        const geometry = row.geometry ? openedRing(row.geometry) : null;
        const feature = { type: "Feature", geometry, properties: row.properties } as Feature;
        if (row.collection_id) feature.collectionId = row.collection_id;
        features[row.feature_id] = feature;
    }
    return features;
}

export type RoleName = "admin" | "editor" | "analyst" | "viewer";

export type SceneLeaf = { id: string; kind: "dataset" | "raster" | "attachment" | "feature" | "collection" };
export type NonEmptyArray<T> = [T, ...T[]];
export type SceneGroup = { id: string; kind: "group"; name: string; children: NonEmptyArray<SceneLeaf> };
export type SceneTreeNode = SceneLeaf | SceneGroup;
export type SceneTree = SceneTreeNode[];

export interface ProjectInfo {
    project_id: string;
    project_name: string;
    org_slug: string;
    created_at: string;
    updated_at: string;
    proj: string | null;
    role_name: RoleName;
    access: ProjectAccess[];
    thumbnail?: string;
    thumbnail_hash?: string;
    basemap_template_url?: string;
    scene_tree: SceneTree;
    classifications: number[];
}

type ProjectEdge = Omit<ProjectInfo, "access"> & { access?: ProjectAccess[] };

export interface ProjectAccess {
    principal: string;
    project_id: string;
    role_name: RoleName;
    created_at: string;
    access_type: string;
    last_seen_at: string | null;
}

export type ProjectUpdate = {
    project_name?: string;
    thumbnail?: string;
    thumbnail_hash?: string;
    basemap_template_url?: string | null;
    scene_tree?: SceneTree;
};

export interface ProjectTask {
    task_id: string;
    task_type: string;
    display_name: string | null;
    status: string;
    progress_pct: number | null;
    phase: string | null;
    error_message: string | null;
    output: Record<string, unknown> | null;
    created_at: string;
    started_at: string | null;
    updated_at: string;
    cancel_requested_at: string | null;
    cancel_requested_by: string | null;
}

export class Project {
    private readonly app: App;
    private readonly auth: Auth;
    private readonly hud: HUD;

    constructor(app: App, auth: Auth, hud: HUD) {
        this.app = app;
        this.auth = auth;
        this.hud = hud;
    }

    get(projectId?: string) {
        let id = projectId;

        if (!id) {
            const parts = window.location.pathname.split("/");
            id = parts[1] === "projects" && parts[2] !== "" ? parts[2] : undefined;
            if (!id) throw new Error("Project ID not found");
        }

        const p = this.app.state.projects[id];
        if (!p) throw new Error("Project not found");

        return p;
    }

    public async create(projectName: string): Promise<ProjectInfo> {
        const response = await this.auth.fetch("/api/projects", {
            method: "POST",
            body: JSON.stringify({ project_name: projectName }),
            headers: {
                "content-type": "application/json",
            },
        });

        if (response.ok) {
            const edge = (await response.json()) as ProjectEdge;
            const p = { ...edge, access: edge.access ?? [] };
            this.app.state.projects[p.project_id] = p;
            return p;
        } else {
            throw new Error(`Failed to create project`);
        }
    }

    public async update(projectId: string, patch: ProjectUpdate) {
        const response = await this.auth.fetch(`/api/projects/${projectId}`, {
            method: "PATCH",
            body: JSON.stringify(patch),
            headers: {
                "content-type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to update project`);
        }
    }

    public async remove(projectId: string): Promise<void> {
        const confirmed = confirm(
            "Are you sure you want to delete this project and associated datasets? This action cannot be undone.",
        );

        if (!confirmed) return;

        const response = await this.auth.fetch(`/api/projects/${projectId}`, {
            method: "DELETE",
            headers: {},
        });

        if (!response.ok) {
            throw new Error(`Failed to delete project`);
        }

        delete this.app.state.projects[projectId];
    }

    public async addDataset(projectId: string, datasetId: string): Promise<void> {
        if (this.app.projectId(datasetId) === projectId) {
            console.warn(`Dataset ${datasetId} is already in project ${projectId}`);
            return;
        }

        const url = `/api/projects/${projectId}/datasets/${datasetId}`;
        const headers = { "content-type": "application/json" };
        const response = await this.auth.fetch(url, { method: "POST", headers });

        if (!response.ok) {
            throw new Error(`Failed to add project dataset`);
        }

        await Promise.all([this.loadProjects(), this.app.loadDatasets()]);
        this.app.state.visible.push(datasetId);
        this.app.adjustRanges(this.extent(projectId));
    }

    public async moveDataset(fromProjectId: string, toProjectId: string, datasetId: string): Promise<void> {
        const url = `/api/projects/${fromProjectId}/datasets/${datasetId}`;
        const response = await this.auth.fetch(url, {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ project_id: toProjectId }),
        });

        if (!response.ok) {
            throw new Error("Failed to move dataset");
        }

        await Promise.all([this.loadProjects(), this.app.loadDatasets()]);

        const visible = this.app.state.visible.filter((id) => id !== datasetId);
        setArray(this.app.state.visible, visible);
        this.app.adjustRanges(this.extent(fromProjectId));
    }

    /** Attaches a raster to `projectId`. The backend POST also handles
     *  reassignment when the raster already belongs to a different project. */
    public async addRaster(projectId: string, rasterId: string): Promise<void> {
        const name = this.app.state.datasets[rasterId]?.name;
        if (!name) throw new Error(`Raster ${rasterId} not found`);

        const url = `/api/projects/${projectId}/rasters/${encodeURIComponent(name)}`;
        const response = await this.auth.fetch(url, {
            method: "POST",
            headers: { "content-type": "application/json" },
        });

        if (!response.ok) {
            throw new Error(`Failed to add raster to project`);
        }

        await Promise.all([this.loadProjects(), this.app.loadDatasets()]);
    }

    /** As for a raster, the backend POST also does a move from another project. */
    public async addAttachment(projectId: string, attachmentId: string): Promise<void> {
        if (!this.app.state.datasets[attachmentId]) throw new Error(`Attachment ${attachmentId} not found`);

        const url = `/api/projects/${projectId}/attachments/${attachmentId}`;
        const response = await this.auth.fetch(url, {
            method: "POST",
            headers: { "content-type": "application/json" },
        });

        if (!response.ok) {
            throw new Error(`Failed to add attachment to project`);
        }

        await Promise.all([this.loadProjects(), this.app.loadDatasets()]);
    }

    public async upsertAccess(projectId: string, principal: string, roleName: RoleName): Promise<void> {
        const p = this.get(projectId);

        const existing = p.access.find((m) => m.principal === principal);
        if (existing) {
            existing.role_name = roleName;
        } else {
            p.access.push({
                principal,
                project_id: projectId,
                role_name: roleName,
                created_at: new Date().toISOString(),
                access_type: "user",
                last_seen_at: null,
            });
        }

        const response = await this.auth.fetch(`/api/projects/${projectId}/access`, {
            method: "POST",
            headers: {
                "content-type": "application/json",
            },
            body: JSON.stringify({ principal, role_name: roleName }),
        });

        if (!response.ok) {
            this.hud.toast("Failed to update project access", "error");
        }
    }

    public async resendInvite(projectId: string, principal: string): Promise<void> {
        const response = await this.auth.fetch(
            `/api/projects/${projectId}/access/${encodeURIComponent(principal)}/resend`,
            {
                method: "POST",
                headers: {},
            },
        );

        if (!response.ok) {
            this.hud.toast("Failed to resend invitation", "error");
        } else {
            this.hud.toast("Invitation resent", "success");
        }
    }

    public async removeAccess(projectId: string, principal: string): Promise<void> {
        const p = this.get(projectId);
        p.access = p.access.filter((m) => m.principal !== principal);

        const response = await this.auth.fetch(`/api/projects/${projectId}/access/${encodeURIComponent(principal)}`, {
            method: "DELETE",
            headers: {},
        });

        if (!response.ok) {
            this.hud.toast("Failed to remove project access", "error");
        }
    }

    public async createLink(projectId: string, roleName: string): Promise<void> {
        const p = this.get(projectId);

        const response = await this.auth.fetch(`/api/projects/${projectId}/links`, {
            method: "POST",
            headers: {
                "content-type": "application/json",
            },
            body: JSON.stringify({ role_name: roleName }),
        });

        if (!response.ok) {
            this.hud.toast("Failed to create project link", "error");
        }

        const link = await response.json();
        p.access.push(link as ProjectAccess);
    }

    private datasetKinds: DatasetKind[] = ["pointcloud", "raster", "vector"];
    public sceneDatasets(projectId?: string): Record<string, DatasetListMeta> {
        const id = projectId ?? this.get().project_id;
        return Object.fromEntries(
            Object.entries(this.app.state.datasets).filter(([, dataset]) => {
                return dataset.project_id === id && this.datasetKinds.includes(dataset.kind);
            }),
        );
    }

    public isEmpty(projectId?: string): boolean {
        return isEmptyObject(this.sceneDatasets(projectId));
    }

    /** Detailed point-cloud metadata belonging to `projectId`, keyed by dataset id. */
    public pointcloudMetadata(projectId?: string): Record<string, DatasetMeta> {
        const id = projectId ?? this.get().project_id;
        return Object.fromEntries(
            Object.entries(this.app.state.pointclouds).filter(([datasetId]) => this.app.projectId(datasetId) === id),
        );
    }

    /** Detailed raster metadata belonging to `projectId`, keyed by raster id. */
    public rasterMetadata(projectId?: string): Record<string, DatasetListItem> {
        const id = projectId ?? this.get().project_id;
        return Object.fromEntries(
            Object.entries(this.app.state.datasets).filter(([datasetId, item]) => {
                return item.kind === "raster" && this.app.projectId(datasetId) === id;
            }),
        );
    }

    public bounds(projectId: string): [number, number, number, number] | null {
        const bounds = Object.values(this.sceneDatasets(projectId))
            .map((dataset) => dataset.bounds_wgs84)
            .filter((bounds): bounds is [number, number, number, number, number, number] => bounds !== null);

        if (bounds.length === 0) {
            return null;
        }

        return [
            Math.min(...bounds.map((b) => b[0])),
            Math.min(...bounds.map((b) => b[1])),
            Math.max(...bounds.map((b) => b[2])),
            Math.max(...bounds.map((b) => b[3])),
        ];
    }

    public extent(projectId: string) {
        const combined = new THREE.Box3();

        for (const item of Object.values(this.sceneDatasets(projectId))) {
            if (!item.bounds_wgs84) continue;

            const dataset = item.kind === "pointcloud" ? this.app.state.pointclouds[item.id] : undefined;
            const zScale = dataset ? this.app.toSceneLength(dataset) : 1;
            const minZ = item.bounds_wgs84[4] * zScale;
            const maxZ = item.bounds_wgs84[5] * zScale;
            combined.union(this.app.boundsExtent(item.bounds_wgs84, minZ, maxZ));
        }

        if (combined.isEmpty()) {
            combined.min = new THREE.Vector3(0, 0, 0);
            combined.max = new THREE.Vector3(1, 1, 1);
        }

        return combined;
    }

    public center(projectId: string) {
        const extent = this.extent(projectId);
        const center = extent.getCenter(new THREE.Vector3());
        center.z = 0;

        return center;
    }

    public async loadProjects(signal?: AbortSignal) {
        const response = await this.auth.fetch("/api/projects", { signal });

        if (response.ok) {
            const edges = (await response.json()) as ProjectEdge[];

            const projects: ProjectInfo[] = edges.map((edge) => ({
                ...edge,
                access: edge.access ?? [],
            }));

            // Assign avatar colors per domain in the order access was granted.
            registerAvatarDomains(
                projects
                    .flatMap((p) => p.access.filter((m) => m.access_type === "user"))
                    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                    .map((m) => m.principal),
            );

            const entries = projects.map((p) => [p.project_id, p]);
            setRecord(this.app.state.projects, Object.fromEntries(entries));
        }
    }

    public getLink(projectId: string, options: { focusId?: string; token?: string; state?: string } = {}) {
        const { focusId, token, state } = options;
        const params = new URLSearchParams();

        if (focusId) params.set("focus-id", focusId);
        if (token) params.set("token", token);
        if (state) params.set("state", btoa(state));

        const url = new URL(window.location.origin);
        url.pathname = `/projects/${projectId}`;
        url.search = params.toString();

        return url.toString();
    }

    public async upsertFeature(projectId: string, featureId: string, feature: Feature): Promise<void> {
        if (!this.canEdit(projectId)) return;

        const response = await this.auth.fetch(`/api/projects/${projectId}/features/${featureId}`, {
            method: "POST",
            body: JSON.stringify({
                geometry: closedRing(feature.geometry),
                properties: feature.properties,
            }),
            headers: {
                "content-type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to upsert feature`);
        }
    }

    // Create a feature collection together with its features in a single request.
    // Returns the new collection id and the created features keyed by feature id.
    public async createCollection(
        projectId: string,
        name: string,
        features: { geometry: Feature["geometry"]; properties: Feature["properties"] }[],
        properties?: GeoJSON.GeoJsonProperties,
        sourceAssetIds: string[] = [],
    ): Promise<FeatureCollection | null> {
        if (!this.canEdit(projectId)) return null;

        const response = await this.auth.fetch(`/api/projects/${projectId}/feature-collections`, {
            method: "POST",
            body: JSON.stringify({
                name,
                properties: properties ?? null,
                source_asset_ids: sourceAssetIds,
                features: features.map((feature) => ({
                    geometry: closedRing(feature.geometry),
                    properties: feature.properties,
                })),
            }),
            headers: {
                "content-type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to create feature collection`);
        } else {
            return (await response.json()) as FeatureCollection;
        }
    }

    public async deleteFeature(projectId: string, featureId: string): Promise<void> {
        if (!this.canEdit(projectId)) return;

        const response = await this.auth.fetch(`/api/projects/${projectId}/features/${featureId}`, {
            method: "DELETE",
            headers: {},
        });

        if (!response.ok) {
            throw new Error(`Failed to delete feature`);
        }
    }

    public async updateCollection(
        projectId: string,
        collectionId: string,
        patch: {
            name?: string;
            properties?: GeoJSON.GeoJsonProperties | null;
        },
    ): Promise<void> {
        if (!this.canEdit(projectId)) return;

        const response = await this.auth.fetch(`/api/projects/${projectId}/feature-collections/${collectionId}`, {
            method: "PATCH",
            body: JSON.stringify(patch),
            headers: {
                "content-type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to update feature collection`);
        }
    }

    public async deleteCollection(projectId: string, collectionId: string): Promise<void> {
        if (!this.canEdit(projectId)) return;

        const response = await this.auth.fetch(`/api/projects/${projectId}/feature-collections/${collectionId}`, {
            method: "DELETE",
        });

        if (!response.ok) {
            throw new Error(`Failed to delete feature collection`);
        }
    }

    public async retryTask(projectId: string, taskId: string): Promise<ProjectTask> {
        const response = await this.auth.fetch(`/api/projects/${projectId}/tasks/${taskId}/retry`, {
            method: "POST",
            headers: { "content-type": "application/json" },
        });

        if (!response.ok) throw new Error(`Failed to retry project task`);

        return (await response.json()) as ProjectTask;
    }

    public async cancelTask(projectId: string, taskId: string): Promise<ProjectTask> {
        const response = await this.auth.fetch(`/api/projects/${projectId}/tasks/${taskId}/cancel`, {
            method: "POST",
        });

        if (!response.ok) throw new Error(`Failed to cancel project task`);

        return (await response.json()) as ProjectTask;
    }

    public async listTasks(
        projectId: string,
        opts?: { status?: string[]; limit?: number; signal?: AbortSignal },
    ): Promise<ProjectTask[]> {
        const params = new URLSearchParams();
        if (opts?.status) params.set("status", opts.status.join(","));
        if (opts?.limit) params.set("limit", opts.limit.toString());
        const url = `/api/projects/${projectId}/tasks?${params}`;
        const init = { headers: {}, signal: opts?.signal };

        const response = await this.auth.fetch(url, init);
        if (!response.ok) throw new Error(`Failed to list project tasks`);

        return (await response.json()) as ProjectTask[];
    }

    // "none" for all features not in a collection
    public async getFeatures(projectId: string, collection: string): Promise<Record<string, Feature>> {
        const query = `?collection=${encodeURIComponent(collection)}`;
        const response = await this.auth.fetch(`/api/projects/${projectId}/features${query}`);

        if (!response.ok) {
            throw new Error(`Failed to get features`);
        }

        return parseFeatureRows((await response.json()) as FeatureRow[]);
    }

    public async getFeatureTile(
        projectId: string,
        collectionId: string,
        z: number,
        x: number,
        y: number,
    ): Promise<Record<string, Feature>> {
        const url = `/api/projects/${projectId}/feature-collections/${collectionId}/tiles/${z}/${x}/${y}`;

        const response = await this.auth.fetch(url);
        if (!response.ok) throw new Error(`Failed to get feature tile ${z}/${x}/${y}`);

        if (response.status === 204) {
            return {};
        } else {
            const rows = (await response.json()) as FeatureRow[];
            return parseFeatureRows(rows);
        }
    }

    public async getCollections(projectId: string): Promise<Record<string, FeatureCollection>> {
        const response = await this.auth.fetch(`/api/projects/${projectId}/feature-collections`);

        if (!response.ok) {
            throw new Error(`Failed to get feature collections`);
        }

        const rows = (await response.json()) as FeatureCollection[];
        return Object.fromEntries(rows.map((row) => [row.collection_id, row]));
    }

    public canEdit(projectId: string | null): boolean {
        if (this.auth.state.orgRole === "org:admin") {
            return true;
        }

        if (projectId === null) return false;

        const p = this.get(projectId);

        return p?.role_name === "admin" || p?.role_name === "editor";
    }

    public canManage(projectId: string): boolean {
        if (this.auth.state.orgRole === "org:admin") {
            return true;
        }

        const p = this.get(projectId);
        return p?.role_name === "admin";
    }

    public canExport(projectId: string): boolean {
        if (this.auth.state.orgRole === "org:admin") {
            return true;
        }

        const p = this.get(projectId);
        return p?.role_name === "admin" || p?.role_name === "editor" || p?.role_name === "analyst";
    }
}
